import { FC } from "react";
import { Box, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { types } from "@vilocnv/allsetra-core";
import { NoDataIcon } from "assets/icons";
import { LAST_TRIPS_PANEL_WIDTH } from "../../constants/lastTrips.constants";
import LastTripsPanelItem from "./LastTripsPanelItem";

interface LastTripsPanelProps {
  open: boolean;
  objectName: string;
  trips: types.IObjectRide[];
  loading: boolean;
  error: string | null;
  selectedTripId: string | null;
  locationsLoading: boolean;
  locationsError: string | null;
  onClose: () => void;
  onSelectTrip: (trip: types.IObjectRide) => void;
  translator: (key: string) => string;
  currentLocale: string;
}

const LastTripsPanel: FC<LastTripsPanelProps> = ({
  open,
  objectName,
  trips,
  loading,
  error,
  selectedTripId,
  locationsLoading,
  locationsError,
  onClose,
  onSelectTrip,
  translator,
  currentLocale
}) => {
  if (!open) return null;

  const renderContent = () => {
    if (loading) {
      return (
        <Stack spacing={1.5} sx={{ p: 2 }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={88} />
          ))}
        </Stack>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error">
            {translator("common.lastTripsLoadError")}
          </Typography>
        </Box>
      );
    }

    if (!trips.length) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ p: 4, textAlign: "center", flex: 1 }}
        >
          <NoDataIcon />
          <Typography color="#646D7B">
            {translator("common.noRecentTrips")}
          </Typography>
        </Stack>
      );
    }

    return (
      <Box sx={{ overflowY: "auto", flex: 1 }}>
        {trips.map((trip) => (
          <LastTripsPanelItem
            key={trip.uniqueId}
            trip={trip}
            selected={selectedTripId === trip.uniqueId}
            onSelect={onSelectTrip}
            translator={translator}
            currentLocale={currentLocale}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        width: LAST_TRIPS_PANEL_WIDTH,
        height: "100%",
        backgroundColor: "#FFFFFF",
        boxShadow: "-4px 0 16px rgba(0, 0, 0, 0.12)",
        zIndex: 5,
        display: "flex",
        flexDirection: "column",
        pointerEvents: "auto"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #E8ECF2",
          minHeight: 56
        }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {translator("common.lastTrips")}
          </Typography>
          <Typography variant="caption" color="#646D7B" noWrap>
            {objectName}
          </Typography>
        </Box>
        <IconButton
          aria-label={translator("buttonsText.close")}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      {renderContent()}

      {locationsLoading ? (
        <Box sx={{ px: 2, py: 1, borderTop: "1px solid #E8ECF2" }}>
          <Typography variant="caption" color="#646D7B">
            {translator("common.loadingRoute")}
          </Typography>
        </Box>
      ) : null}

      {locationsError ? (
        <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #E8ECF2" }}>
          <Typography variant="caption" color="error">
            {locationsError === "private_ride"
              ? translator("common.privateTripCannotBeDisplayed")
              : locationsError === "no_route_data"
                ? translator("descriptions.rideDataNotAvialble")
                : translator("common.lastTripsLoadError")}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default LastTripsPanel;
